import axios from 'axios';
import { useAuthStore } from '../store/useAuthStore'; // Import Zustand auth store
import i18n from '../i18n'; // Import i18n for language detection

const http = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL, // Updated for Vite compatibility
});

const getHeaders = (isFormData = false) => {
  const token = useAuthStore.getState().token; // Get token directly from Zustand store
  const language = i18n.language.toUpperCase(); // Get current language from i18n and convert to uppercase
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get user's timezone

  const headers = {
    Authorization: token ? `${token}` : '',
    'private-api-key': import.meta.env.VITE_PRIVATE_API_KEY, // Add private API key to headers
    'x-lang': language, // Add language to headers
    'x-timezone': timezone, // Include timezone in custom header
    'x-platform': 'WEB', // Always set platform as WEB for this client
  };

  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  } else {
    // Don't manually set 'Content-Type' for FormData, browser will handle it
    delete headers['Content-Type'];
  }

  return headers;
};

// Response Interceptor
http.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const message =
        error.response.data.message || error.response.data.status_message;

      // Handle token expiration or invalid token scenarios
      if (
        message &&
        (message.includes('valid token') || message.includes('expired') || message.includes('doldu'))
      ) {
        useAuthStore.getState().clearAuth(); // Clear auth state on token expiration
        window.location.reload(); // Reload the page to reset
      }
    }

    return Promise.reject(error);
  }
);

// HTTP Methods
const get = async (url, params = {}, options = {}) => {
  const { headers: extraHeaders, ...restOptions } = options;
  return await http.get(url, {
    headers: extraHeaders ? { ...getHeaders(), ...extraHeaders } : getHeaders(),
    params,
    ...restOptions,
  });
};

const post = async (url, data, options = {}) => {
  const isFormData = options.isFormData || data instanceof FormData;
  return await http.post(url, data, {
    headers: getHeaders(isFormData),
    ...options,
  });
};

const put = async (url, data) => {
  return await http.put(url, data, { headers: getHeaders() });
};

const _delete = async (url) => {
  return await http.delete(url, { headers: getHeaders() });
};

// Exported Service
export const httpService = {
  get,
  post,
  put,
  delete: _delete,
  getHeaders, // Export getHeaders to use in other requests
};
